import { useNavigate } from 'react-router-dom';
import { Users, UserCircle, LogIn, Volume2 } from 'lucide-react';
import { useState } from 'react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [audioEnabled, setAudioEnabled] = useState(false);

  return (
    <div className="min-h-screen relative">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1766808982363-7df749493298?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxlbGRlcmx5JTIwY2FyZSUyMHN1cHBvcnQlMjBraW5kbmVzc3xlbnwwfHx8fDE3NzEwNjQ1OTh8MA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark gradient overlay for readability */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.75) 0%, rgba(30, 41, 59, 0.70) 50%, rgba(19, 78, 74, 0.75) 100%)' 
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="flex justify-center mb-6 animate-scale-in">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 hover-lift hover-glow">
              <img 
                src="/images/svdp-logo.jpg" 
                alt="SVdP Chicago" 
                className="h-20 md:h-24 w-auto"
                data-testid="svdp-logo"
              />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 animate-slide-up delay-100" style={{ fontFamily: 'Playfair Display, serif' }}>
            Request Assistance
          </h1>
          <p className="text-lg md:text-xl text-slate-300 animate-slide-up delay-200">SVdP Mary, Mother of God Parish</p>
          <p className="text-base text-slate-400 animate-slide-up delay-300 italic">Journeying with our neighbors in faith and service</p>
          <p className="text-sm text-slate-500 animate-slide-up delay-300">Serving the Uptown & Edgewater communities of Chicago</p>
          <p className="text-base text-slate-400 mt-2 animate-slide-up delay-300">We are here to help where we can</p>
        </div>

        {/* Audio Assistance Toggle - Like Papal Care */}
        <div className="max-w-md mx-auto mb-8 animate-fade-in delay-100">
          <div className="audio-bar">
            <Volume2 className="w-5 h-5 text-slate-400" />
            <span className="text-slate-300 text-sm">Audio Assistance</span>
            <span className="text-slate-500 text-xs hidden sm:inline">Read form questions aloud</span>
            <button
              onClick={() => setAudioEnabled(!audioEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${audioEnabled ? 'bg-[#0072BC]' : 'bg-slate-700'}`}
              data-testid="audio-toggle"
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${audioEnabled ? 'left-7' : 'left-1'}`} />
            </button>
            <span className="text-slate-400 text-sm">{audioEnabled ? 'On' : 'Off'}</span>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="max-w-2xl mx-auto mb-10 animate-fade-in delay-200">
          <div className="emergency-banner flex items-center gap-3" data-testid="emergency-notice">
            <div className="w-1 h-full bg-rose-400 rounded-full self-stretch" />
            <p className="text-rose-300 font-medium text-sm md:text-base">
              If this is an emergency, please call 911 or 311 for immediate assistance.
            </p>
          </div>
        </div>

        {/* Welcome Section */}
        <div className="text-center mb-10 animate-fade-in delay-200">
          <div className="glass-panel max-w-xl mx-auto p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Welcome
            </h2>
            <p className="text-slate-300 text-base md:text-lg">
              We are here to journey with you. Assisting you where we can. Let us help you.
            </p>
          </div>
        </div>

        {/* How Would You Like to Proceed */}
        <div className="max-w-xl mx-auto mb-8 animate-slide-up delay-500">
          <div className="glass-panel p-6 md:p-8 card-hover-border">
            <h3 className="text-xl md:text-2xl font-medium text-white text-center mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              How would you like to proceed?
            </h3>
            
            <div className="space-y-4">
              {/* I Need Assistance Button */}
              <button
                onClick={() => navigate('/intake/self')}
                className="w-full bg-[#0072BC] hover:bg-[#005a9e] text-white rounded-xl px-6 py-4 font-medium transition-all shadow-lg shadow-blue-900/30 hover:shadow-blue-900/50 hover:-translate-y-1 flex items-center justify-center gap-3 text-lg btn-press"
                data-testid="self-service-btn"
              >
                <UserCircle className="w-6 h-6" />
                I Need Assistance
              </button>

              {/* Staff Entry Button */}
              <button
                onClick={() => navigate('/intake/staff')}
                className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-xl px-6 py-4 font-medium transition-all backdrop-blur-sm flex items-center justify-center gap-3 text-lg hover-lift btn-press"
                data-testid="staff-entry-btn"
              >
                <Users className="w-6 h-6" />
                Parish Staff Entry
              </button>
            </div>
          </div>
        </div>

        {/* Volunteer Login Link */}
        <div className="text-center animate-fade-in delay-500">
          <p className="text-slate-400 mb-3 text-sm">Are you a Vincentian volunteer?</p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 text-[#0072BC] hover:text-[#60a5fa] font-medium transition-colors group hover-glow px-4 py-2 rounded-full"
            data-testid="volunteer-login-link"
          >
            <LogIn className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            Vincentian Volunteer Login
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <p className="text-slate-500 text-sm">
            If you need assistance, please ask a staff member or email{' '}
            <a href="mailto:support@svdp-at-mmog.org" className="text-[#0072BC] hover:underline">
              support@svdp-at-mmog.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
